import { isDefined } from "@clipboard-health/util-ts";
import { type VisitRecommendedRateParameters } from "@src/appV2/FeatureFlags/CbhFeatureFlags";

import rateConfigurationData from "./rate-configuration.json";

export type FallbackRateRegion = keyof typeof rateConfigurationData;

const stateAbbreviationMap: Record<string, FallbackRateRegion> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export function getFallbackRecommendedRate(
  address: string | undefined
): VisitRecommendedRateParameters {
  const region = stateFromPatient(address);

  return rateConfigurationData[region];
}

export function stateFromPatient(address: string | undefined): FallbackRateRegion {
  if (!isDefined(address)) {
    return "Default";
  }

  const abbreviatedState = parseStateFromFormattedAddress(address);
  if (!isDefined(abbreviatedState)) {
    return "Default";
  }

  const state = stateAbbreviationMap[abbreviatedState];

  return isDefined(state) ? state : "Default";
}

/**
 * The following methods are stolen from
 * https://github.com/ClipboardHealth/clipboard-health/blob/68ec71735b700251bbc5a874d83edf0bb5d19da2/src/modules/placement-candidates/jobs/placement-scraping-workplace-index-run-step.job.ts#L236
 */
function parseStateFromFormattedAddress(address: string): string | undefined {
  const parts = address.split(",").map((part) => part.trim());

  if (parts.length < 3) {
    return undefined;
  }

  const city = parts.at(1)?.trim();
  const stateWithZip = parts.at(2)?.trim();

  const stateWithZipParts = stateWithZip?.split(" ");
  const state = stateWithZipParts?.at(0)?.trim();
  const zip = stateWithZipParts?.at(1)?.trim();

  // valid expected format: "[city], [state] [zip]"
  if (city && state && zip && isStateCode(state) && isZipCode(zip)) {
    return state;
  }

  return undefined;
}

function isStateCode(value: string): boolean {
  return /^[A-Z]{2}$/.test(value);
}

function isZipCode(value: string): boolean {
  return /^\d{5}$/.test(value);
}
